import React, { useContext, useEffect, useState } from "react";
import {
  Datepicker,
  MbscCalendarEventData,
  MbscDatepickerCellClickEvent,
  MbscSelectedDateChangeEvent,
} from "@mobiscroll/react";
import { Link } from "react-router-dom";
import { Eventcalendar } from "@mobiscroll/react";
import "../../assets/scss/custom/_custom.scss";
import { createAppointmentApi } from "../../api-helpers/AppointmentApiHelper";
import { appointmentDetailsPath, isLegacyAcsVisit } from "../../constants/Appointment";
import { GridDataRequest, GridDataResponse } from "patients-experience-client";
import {
  formatDateTimeToLocal,
} from "../schedule/dateFormat";
import { DatepickerBase } from "@mobiscroll/react/dist/src/core/components/datepicker/datepicker";
import { UsersDto } from "users-experience-client";
import Spinner from "../../components/Spinner";
import { createWorkflowApi } from "../../api-helpers/CommonUtilsApiHelper";
import { WorkflowConstants } from "../../helper/WorkflowConstants";
import { DateTime } from "luxon";
import useLoggedInAccount from "../../auth/LoggedInAccountInfo";
import { ScheduleInformation, ScheduleItem } from '@microsoft/microsoft-graph-types'
import classnames from 'classnames'
import { UserContextHolder } from "../../auth/UserContextHolder";
import { getTzIdentifier, TimeZoneKey } from "../../helper/momentTimezoneMapping";
import { Event, EventType } from "../../interfaces/event";
import { UserEventsResult } from "appointment-experience-client";
import { ReactComponent as OutlookCalendar } from "../../assets/img/outlook-calendar.svg";
import { WorkflowState } from "common-util-experience-client";

interface CustomEvent extends MbscCalendarEventData {
  description: string;
}

export const Appointments = () => {
  const [date, setDate] = useState<Date>(new Date());
  const appointmentApi = createAppointmentApi();
  const [viewType, setViewType] = useState<"list" | "calendar">("calendar");
  const [loading, setLoading] = useState<boolean>(true);
  const [openPicker, setOpenPicker] = useState<boolean>(false);

  const workflowApi = createWorkflowApi();

  const changeHandler = (
    args: MbscDatepickerCellClickEvent,
    inst: DatepickerBase
  ) => {
    loadData(new Date(args.date), new Date(args.date));
    setDate(args.date);
  };

  const [myEvents, setMyEvents] = useState<Event[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [user, setUser] = useState<UsersDto>();

  const { loggedInAccount } = useLoggedInAccount();

  useEffect(() => {
    const userListener = () => {
      setUser(UserContextHolder.getUser());
    };

    UserContextHolder.addListener(userListener);

    return () => {
      UserContextHolder.removeListener(userListener);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }
    loadData(new Date(), new Date());
  }, [user]);

  const personalCalendarEventCollection: Event[] = [];

  const loadData = async (
    startDate: Date,
    endDate: Date
  ): Promise<GridDataResponse | undefined> => {
    setLoading(true);
    const startLocalDateTime = DateTime.fromJSDate(startDate);
    const endLocalDateTime = DateTime.fromJSDate(endDate);

    const startOfDayUTC = startLocalDateTime.startOf("day").toUTC().toISO();
    const endOfDayUTC = endLocalDateTime
      .endOf("day")
      .plus({ milliseconds: 1 })
      .toUTC()
      .toISO();

    const scheduleItems = await fetchPersonalCalendarAppointments(loggedInAccount!.username, startOfDayUTC!, endOfDayUTC!) || [];
    scheduleItems.map((scheduleItem: any, idx) => {
      populateScheduleItems(scheduleItem, idx);
    });

    const request: GridDataRequest = {
      startRow: 0,
      endRow: Number.MAX_SAFE_INTEGER,
      sortCriteria: [
        {
          propertyName: "time",
          descending: false,
        },
      ],
      filters: [
        {
          propertyName: "time",
          operator: "greater-than-equals",
          propertyValue: new Date(startOfDayUTC!),
        },
        {
          propertyName: "time",
          operator: "less-than-equals",
          propertyValue: new Date(endOfDayUTC!),
        },
        {
          propertyName: "coachUserId",
          operator: "in",
          propertyValue: [user?.id],
        },
      ],
    };

    try {
      const result = await appointmentApi.appointmentControllerGetGridData(
        request
      );

      const response = await workflowApi.workflowControllerGetStatesForWorkflow(
        WorkflowConstants.appointmentWorkflowType
      );
      const workflowStates = response.data;

      // Extract the state IDs for "Planned", "In-Progress", "Rescheduled" and "Completed"
      const targetStateIds = workflowStates.filter((state) =>
        ["Planned", "In-Progress", "Rescheduled", "Completed"].includes(state.stateName)
      ).map((state) => state.stateId);

      // Filter appointments with wfStateId matching one of the target state IDs
      const filteredAppointments = result.data.data.filter(
        (appointment) =>
          targetStateIds.includes(appointment.wfStateId)
      );
      let filteredCalendarEvent: Event[] = personalCalendarEventCollection;
      const formattedEvents = filteredAppointments.map((appointment) => {
        const start = new Date(appointment.time);
        const end = new Date(
          start.getTime() + appointment.durationMinutes * 60000
        );
        // Filtering Outlook event to not show them double in the Appointments Calendar
        filteredCalendarEvent = filteredCalendarEvent.filter((outlookAppointment) => (appointment.externalAppointmentId != outlookAppointment.id));
        return {
          id: appointment.appointmentId,
          start,
          end,
          title: appointment.patientName,
          description: appointment.appointmentType,
          type: EventType.Spire
        };
      });

      setMyEvents([...formattedEvents, ...filteredCalendarEvent]);
      setAppointments(filteredAppointments);
      return result.data;
    } catch (error) {
      console.error("Error loading data:", error);
      setLoading(false);
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonalCalendarAppointments = async (userEmail: string, startDateUTC: string, endDateUTC: string): Promise<UserEventsResult[]> => {
    try {
      const response =
        await appointmentApi.appointmentControllerGetUserEvents({
          userEmail: userEmail,
          startTime: new Date(startDateUTC),
          endTime: new Date(endDateUTC),
        }
        );

      const scheduleInformation = response.data as UserEventsResult[];
      return scheduleInformation ?? [];
    }
    catch (error) {
      console.error("Error fetching data: ", error);
      return [];
    }

  }

  const populateScheduleItems = (scheduleItem: any, idx: number) => {
    if (scheduleItem.start?.dateTime && scheduleItem.end?.dateTime) {
      const start = DateTime.fromJSDate(
        new Date(
          DateTime.fromISO(scheduleItem.start?.dateTime, { zone: "utc" }).toISO()!
        )
      );

      const end = DateTime.fromJSDate(
        new Date(
          DateTime.fromISO(scheduleItem.end?.dateTime, { zone: "utc" }).toISO()!
        )
      );

      const appointmentStartTime = new Date(
        start.year,
        start.month - 1,
        start.day,
        start.hour,
        start.minute,
        start.second,
        start.millisecond
      );


      const appointmentEndTime = new Date(
        end.year,
        end.month - 1,
        end.day,
        end.hour,
        end.minute,
        end.second,
        end.millisecond
      );
      const diffInMillis = Math.abs(appointmentEndTime.getTime() - appointmentStartTime.getTime());

      // Convert milliseconds to minutes
      const diffInMinutes = diffInMillis / (1000 * 60);

      personalCalendarEventCollection.push({
        id: scheduleItem.id ?? idx.toString(),
        start: appointmentStartTime,
        end: appointmentEndTime,
        title: scheduleItem.subject ?? '',
        description: diffInMinutes >= 30 ? 'Outlook Calendar Event' : '',
        type: EventType.Outlook,
      });
    }
  }

  const customEventRender = (event: CustomEvent) => {
    return (
      <a href={`/schedule/charting/${event.id}`} className={classnames('event-link', {
        'disabled': event?.original?.type === EventType.Outlook,
      })}>
        <div className={classnames("event-container", `${event?.original?.type}-event`)}>
          <div className="event-header">
            <div className="event-title">
              <div className="event-time">
                <span className="event-title-icon"> {event?.original?.type === EventType.Outlook && <OutlookCalendar />} </span>
                {event.start} - {event.end} - {event.title}
              </div>
            </div>

          </div>
          <div className="event-description">{event.original?.description}</div>
        </div>
      </a>
    );
  };

  const handleSelectedDateChange = (args: MbscSelectedDateChangeEvent) => {
    let selectedDate: Date;

    if (typeof args.date === "string" || args.date instanceof Date) {
      selectedDate = new Date(args.date);
    } else {
      console.error("Selected date is not a valid type:", args.date);
      return;
    }

    const startDate = new Date(selectedDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(selectedDate);
    endDate.setHours(23, 59, 59, 999);
    setDate(startDate);
    loadData(startDate, endDate);
  };

  const openCalenderView = async (date: Date) => {
    setViewType("calendar");
    setDate(date);
    const startDate = new Date(date);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(date);
    endDate.setHours(23, 59, 59, 999);
    await loadData(new Date(startDate), new Date(endDate));
  };

  return (
    <>
      {loading && <Spinner />}
      <div className="sidebar sidebar-dark h-100 d-flex flex-column">
        <div className="sidebar__header pt-3 pb-0">
          <div className="row d-flex align-items-center justify-content-between text-white fs-12">
            <div className="col-4"></div>
            <div className="col-4 text-center">
              {viewType === "list" ? (
                <div className="calendar-link" onClick={() => setOpenPicker(!openPicker)}>
                  <Datepicker
                    controls={["calendar"]}
                    themeVariant="light"
                    value={date}
                    onCellClick={changeHandler}
                    dateFormat="MMM DD, YYYY"
                    inputTyping={false}
                    isOpen={openPicker}
                  />
                  <span className="material-symbols-outlined fs-20 carrot-icon">
                    keyboard_arrow_down
                  </span>
                </div>
              ) : null}
            </div>
            <div className="col-4 d-flex">
              <div className="input-group justify-content-end">
                {/* Button to toggle to grid view */}
                <button
                  className={`btndashboard btn-sm btn-white py-05 rounded-start ${viewType === "calendar" ? "active-view" : ""
                    }`}
                  type="button"
                  onClick={()=> openCalenderView(date)}
                >
                  <span className="material-symbols-outlined fs-14 align-text-top">
                    grid_view
                  </span>
                </button>

                <div className="bg-light h-100 w-px z-index-9 opacity-25"></div>

                {/* Button to toggle to list view */}
                <button
                  className={`btndashboard btn-sm btn-white py-05 rounded-end ${viewType === "list" ? "active-view" : ""
                    }`}
                  type="button"
                  onClick={() => setViewType("list")}
                >
                  <span className="material-symbols-outlined fs-14 align-text-top">
                    format_list_bulleted
                  </span>
                </button>
              </div>
            </div>
          </div>
          <hr className="my-2 opacity-25" />
          <div className="d-flex align-items-center">
            <div className="flex-fill">
              <h3 className="text-white fs-18 m-0">Upcoming {viewType === "list" && 'Patient'} Appointments</h3>
            </div>
            <div className="ps-4">
              <Link
                to="/schedule"
                className="a-link a-underline text-info fs-12 text-nowrap"
              >
                View all
              </Link>
            </div>
          </div>
        </div>

        <div className="sidebar__content bg-teal-062 py-3 rounded z-0">
          {appointments?.length === 0 && viewType === "list" ? (
            <div className="d-flex d-block">
              <span className="alert alert-secondary border-0 w-100 text-left my-2 fs-6">
                <span className="material-symbols-outlined align-top me-1">info</span> No Appointments for{" "}
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })}
              </span>
            </div>
          ) : null}
          {viewType === "list" ? (
            <div
              className="bg-white rounded p-0 sidebar__content-scroll"
              style={{ height: "auto" }}
            >
              {appointments?.map((appointment) => (
                <>
                  <div className="d-flex p-3" key={appointment.appointmentId}>
                    <div>
                      <a href="#" className="d-block user-icon me-2">
                        <img
                          src="https://cdn-icons-png.flaticon.com/512/168/168719.png"
                          alt={appointment.patientName}
                          className="img-fluid"
                        />
                      </a>
                    </div>
                    <div className="flex-fill">
                      <div className="d-flex">
                        <div className="flex-fill lh-sm">
                          <a className="fw-bold" href="#">
                            {appointment.patientName}
                          </a>
                          <div className="fs-10 text-light">
                            {appointment.appointmentMode} |{" "}
                            {appointment.categoryName} {" Appointment"}
                          </div>
                        </div>
                        <div className="ps-4">
                          <span className="d-block fs-11 text-light">
                            {appointment?.time &&
                              formatDateTimeToLocal(
                                appointment.time as unknown as string
                              )}{" "}
                            {/* Adjust format as needed */}
                          </span>
                        </div>
                      </div>
                      <div className="fs-13 mt-2 text-light">
                        {appointment.appointmentType}{" "}
                        {/* Adjust this if there's specific description data */}
                      </div>
                      <div className="text-end">
                        <Link
                          className="a-link text-info fs-12"
                          to={`/schedule/charting/${appointment.appointmentId}`}
                        >
                          View
                        </Link>
                        {!isLegacyAcsVisit(appointment) && (
                          <a
                            className="a-link text-info fs-12 ms-5"
                            href={appointmentDetailsPath(
                              appointment.appointmentId,
                              appointment.patientId
                            )}
                            target="_blank" rel="noreferrer"
                          >
                            Start
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <hr className="m-0" />
                </>
              ))}
            </div>
          ) : (
            <Eventcalendar
              data={myEvents}
              defaultSelectedDate={date}
              view={{
                schedule: {
                  type: "day",
                  startDay: 1,
                  startTime: "00:00",
                  endTime: "24:00",
                  timeCellStep: 30,
                  timeLabelStep: 30,
                  allDay: true,
                },
              }}
              theme="ios"
              themeVariant="light"
              clickToCreate={false}
              dragToCreate={false}
              renderScheduleEvent={customEventRender}
              onSelectedDateChange={handleSelectedDateChange}
            />
          )}
        </div>
      </div>
    </>
  );
};
